// Антизлоупотребление и жалобы: правила без базы. SMY-80 · P7 Э7.2
//
// ЗАЧЕМ ЭТО ОТДЕЛЬНО ОТ ЛИМИТОВ ТАРИФА. Лимит тарифа — про деньги: сколько
// человек купил, столько и получит. Здесь про ФОРМУ пользования: сто вопросов
// за месяц — это тариф, сто вопросов за пять минут — это скрипт, и никакой
// месячный счётчик его не остановит, пока не кончится месяц.
//
// ЗАЧЕМ ЭТО ВООБЩЕ. Не из строгости, а из договоров. Условия поставщиков
// запрещают перепродажу «через простой вызов функций API» и выдачу ключей
// (docs/legal/provider-terms.md, Яндекс п. 3.15). Продукт, которым пользуются
// как прокси к моделям, нарушает их независимо от того, что написано у нас на
// сайте. Технические меры — часть доказательства, что он устроен иначе.
//
// ГРАНИЦА ЭТОГО ФАЙЛА. Здесь только правила и числа: их же читает клиент,
// когда объясняет человеку сроки и отказы. Всё, что требует базы — счёт
// запусков, журнал сигналов, блокировка, — живёт в API.

import { foldForMatch } from './fold.ts'

/** Пороги. Собраны в одном месте: это политика, а не константы по коду. */
export const ABUSE = {
  /**
   * Запусков в минуту на человека.
   *
   * Десять — заведомо выше живого пользования: один запуск идёт от пяти
   * секунд до сорока, и человек, нажимающий чаще, не успевает прочитать даже
   * первый ответ. Ниже ставить нельзя: несколько быстрых уточнений подряд —
   * нормальный разговор, и упереться в потолок посреди него хуже, чем
   * пропустить скрипт на минуту.
   */
  runsPerMinute: 10,
  /**
   * Запусков в час.
   *
   * Шестьдесят — это час непрерывной работы без единой паузы. Потолок не про
   * деньги (их считает тариф), а про форму: он не даёт выработать месячный
   * лимит Pro за десять минут и заодно делает скриптовое пользование
   * невыгодным — ровно то, ради чего он и заведён.
   */
  runsPerHour: 60,
  /**
   * Распознаваний запроса (`POST /v1/intent`) в минуту и в час на человека. SMY-329
   *
   * Каждое распознавание — вызов модели за наши деньги (около 0,05 ₽,
   * `economics/cogs.md`), а до этого предела щит частоты пускал триста в минуту
   * на сеанс. Числа те же, что у запусков (решение владельца 17.09.2026): клиент
   * зовёт распознавание один раз на отправленное сообщение и ещё раз на ответ на
   * уточнения, так что человек в предел не упирается.
   *
   * Счёт отдельный от запусков: запуск идёт следом за распознаванием, и общий
   * счёт делил бы запас пополам.
   */
  intentsPerMinute: 10,
  intentsPerHour: 60,
  /**
   * Распознаваний всех гостей одного адреса в час. SMY-340
   *
   * Гостевой аккаунт бесплатен и заводится до двадцати в час с адреса, так что
   * предел на человека множился ими: 480 гостей за сутки по шестьдесят в час.
   * Триста — пять гостей, распознающих без остановки, разом: за одним адресом
   * стоят семья, офис или мобильный оператор, а к пределу на человека это не
   * прибавка, а общий потолок. Расход при этом — до 15 ₽ в час с адреса при
   * 0,05 ₽ за вызов, а не 1 440 ₽. Аккаунтов с подтверждённой почтой или номером
   * счёт не касается.
   */
  guestIntentsPerAddressPerHour: 300,
  /**
   * Одинаковых запросов подряд, после которых это уже не человек.
   *
   * Человек, повторяющий вопрос, меняет формулировку — он же не получил
   * ответа, который хотел. Пять запросов, совпадающих ЗНАК В ЗНАК, — это
   * цикл в коде.
   */
  identicalInARow: 5,
  /**
   * Сколько разных сигналов копится до блокировки.
   *
   * Три, и обязательно разных: один сигнал бывает у живого человека —
   * заклинило кнопку, любопытство про устройство продукта. Три разных за
   * сутки — это уже не совпадение.
   */
  distinctSignalsBeforeBlock: 3,
  /** За какой срок считаются сигналы. */
  signalWindowHours: 24,
  /** Срок рассмотрения жалобы на содержание. */
  complaintDays: 5,
  /**
   * Срок рассмотрения оспаривания блокировки. Короче обычного намеренно:
   * человек, заблокированный по ошибке, всё это время не может пользоваться
   * тем, за что заплатил.
   */
  appealDays: 3,
  /**
   * Обращений одного человека за сутки, скользящим окном. SMY-231
   *
   * Живой человек жалуется редко: несколько неудачных результатов за вечер и
   * оспаривание блокировки — это пять, а не десять. Потолок не про очередь
   * разбора, а про объём: обращение до 2000 знаков лежит в базе, уходит в
   * выгрузку целиком и читается списком «Мои обращения» за раз. Без потолка
   * гость писал их по триста в минуту, и 30 000 штук роняли api одной выгрузкой
   * (замер — `apps/api/src/account/export-gate.ts`). С потолком год такого
   * упорства — 3650 обращений, около 16 МБ выгрузки.
   */
  complaintsPerDay: 10,
} as const

// --- Сигналы ------------------------------------------------------------------

export const ABUSE_SIGNALS = [
  /** Частота выше человеческой. */
  'flood',
  /** Одинаковые запросы подряд — цикл, а не разговор. */
  'identical',
  /** Попытка вытащить системный промпт или обойти инструкции. */
  'prompt_probe',
] as const

export type AbuseSignal = (typeof ABUSE_SIGNALS)[number]

/**
 * Попытка вытащить наш системный промпт или снять инструкции.
 *
 * ПОЧЕМУ ЭТО ПРИЗНАК ПРОКСИРОВАНИЯ, а не любопытства. Системный промпт — это
 * и есть разница между «Смыслика выполняет задачу» и «Смыслика даёт доступ к
 * модели». Тот, кто его вытаскивает, обычно собирается обойтись без нас.
 *
 * ГЛАВНАЯ ОСТОРОЖНОСТЬ ЗДЕСЬ — НЕ СЛОМАТЬ ЧЕСТНУЮ ЗАДАЧУ. «Напиши системный
 * промпт для моего бота» — обычная работа, и продукт обязан её делать. Поэтому
 * признаки требуют указания на НАС: «твой промпт», «свои инструкции»,
 * «предыдущие указания». Без притяжательного слова правило не срабатывает.
 */
//
// ⚠️ ХВОСТЫ СЛОВ ПИШУТСЯ КАК `[а-я]*`, А НЕ `\w*`. В JS класс `\w` — это
// `[A-Za-z0-9_]` с флагом `u` и без него, и русской буквы в нём нет: `систем\w*` на слове
// «системный» останавливается после «систем», а дальше по шаблону ждёт пробел.
// Правило от этого не падает, а тихо перестаёт срабатывать. Тот же промах уже
// был с `\b` в apps/api/src/ai/prohibited.ts.
const PROBES: readonly RegExp[] = [
  /(?:тво(?:й|и|его|их|ю)|ваш(?:|и|его|их|у)|сво(?:й|и|его|их|ю))\s+(?:систем[а-я]*\s+)?(?:промпт|инструкц[а-я]*|указан[а-я]*|правил[а-я]*\s+выше)/,
  /(?:покажи|выведи|повтори|напечатай|скажи)\s+(?:мне\s+)?(?:весь\s+|всё\s+|все\s+)?(?:систем[а-я]*\s+промпт|промпт\s+целиком|инструкц[а-я]*\s+целиком)/,
  // «Какие у тебя инструкции» — тоже про нас, хотя притяжательного слова нет.
  // Само по себе это скорее любопытство, чем проксирование, и одного сигнала
  // для блокировки не хватает: их нужно три разных за сутки.
  /у\s+(?:тебя|вас)\s+(?:за\s+)?(?:систем[а-я]*\s+)?(?:промпт|инструкц[а-я]*|указан[а-я]*)/,
  /(?:что|какие)\s+(?:тебе\s+)?(?:сказали|велели|написали|задали)\s+(?:в\s+начале|до\s+меня|выше)/,
  /(?:повтори|процитируй)\s+(?:весь\s+)?текст\s+выше/,
  /(?:игнорируй|забудь|отмени)\s+(?:все\s+)?(?:предыдущ[а-я]*|прежн[а-я]*|свои)\s+(?:инструкц[а-я]*|указан[а-я]*|правил[а-я]*)/,
  /ignore\s+(?:all\s+)?(?:previous|prior|above)\s+instructions/,
  /(?:reveal|show|print|repeat)\s+(?:me\s+)?(?:your\s+)?system\s+prompt/,
  /disregard\s+your\s+instructions/,
]

/** Та же нормализация, что у фильтра запрещённого и тем риска (SMY-316). */
const normalize = foldForMatch

export const probesSystemPrompt = (text: string): boolean => {
  const lower = normalize(text)
  return PROBES.some((p) => p.test(lower))
}

/**
 * Отпечаток запроса — для сравнения «то же самое ли это».
 *
 * Не сам текст: журнал злоупотреблений не должен становиться вторым местом,
 * где живёт содержимое запросов (docs/data-flow.md). Сравнить два отпечатка
 * можно, прочитать по нему запрос — нет.
 *
 * Нормализация до отпечатка снимает пробелы и регистр: цикл, подставляющий
 * номер строки, не должен считаться пятью разными запросами.
 */
export const fingerprintOf = (text: string): string => normalize(text).replace(/\s+/g, ' ').trim()

// --- Жалобы -------------------------------------------------------------------

export const COMPLAINT_KINDS = [
  /** Жалоба на содержание результата. */
  'content',
  /** Оспаривание блокировки. */
  'appeal',
] as const
export type ComplaintKind = (typeof COMPLAINT_KINDS)[number]

export const COMPLAINT_STATUSES = ['new', 'resolved', 'rejected'] as const
export type ComplaintStatus = (typeof COMPLAINT_STATUSES)[number]

/** Сколько дней на ответ. Число одно на клиента и сервер — обещание общее. */
export const daysToAnswer = (kind: ComplaintKind): number =>
  kind === 'appeal' ? ABUSE.appealDays : ABUSE.complaintDays

/**
 * Срок ответа от момента обращения.
 *
 * Считается один раз при приёме и хранится, а не выводится при показе:
 * пороги меняются, а обещание, данное человеку в понедельник, менять задним
 * числом нельзя.
 */
export const dueAtFor = (kind: ComplaintKind, at: Date): Date =>
  new Date(at.getTime() + daysToAnswer(kind) * 24 * 60 * 60 * 1000)

/**
 * Что видит человек, отправив обращение.
 *
 * Срок называется числом, а не «в ближайшее время»: «рассмотрим в ближайшее
 * время» — это отсутствие срока, записанное вежливыми словами.
 */
export const complaintPromise = (kind: ComplaintKind): string =>
  kind === 'appeal'
    ? `Разберёмся за ${daysToAnswer(kind)} дня и ответим сюда же. Пока идёт разбор, блокировка остаётся.`
    : `Рассмотрим за ${daysToAnswer(kind)} дней и ответим сюда же. Если пожаловались на результат — он останется доступным вам, пока идёт разбор.`

/** Ограничение длины обращения. Не про базу, а про то, что его читает человек. */
export const COMPLAINT_MAX_CHARS = 2000
